import { FarmDetails } from "shared/api/apiModels";
import FarmDetailsApi from "shared/api/FarmDetailsApi";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setFarmDetails,
  setCurrentField,
} from "features/panorama/details-slice";
import { safelyParseComma, sceneStartingContent } from "shared/helpers/krpano";
import { useEffect, useState } from "react";

type UseGetFarmDetails = (krpanoLoaded: boolean) => boolean;

/**
 *
 * @param krpanoLoaded see `useEmbedPano`
 * @returns `farmLoaded` - `true` if farm details are loaded
 */
export const useGetFarmDetails: UseGetFarmDetails = (krpanoLoaded) => {
  const dispatch = useAppDispatch();
  const [farmLoaded, setFarmLoaded] = useState<boolean>(false);
  const guid = useAppSelector((state) => state.panorama.guid);
  const currentField = useAppSelector((state) => state.panorama.currentField);

  useEffect(() => {
    const getFarmDetails = async (farmId: string) => {
      window.krpano.call("delete_all_scenes();");
      const response = await FarmDetailsApi.getFarmDetails(farmId);
      if (response.success && response.data) {
        const { fields, default_field_id } = response.data;
        dispatch(setFarmDetails(response.data));
        addFieldsToKrpano(response.data);
        // Attempt 1:
        // - Find current field using `fid`
        // - If current field is acutally an index, use that instead (0-based)
        let fieldId = fields?.find(
          (field, fieldIndex) =>
            field.fid === currentField || fieldIndex.toString() === currentField
        )?.fid;
        // Attempt 2:
        // - Use `default_field_id` to compare against `fid`
        // - If `default_field_id` is acutally an index, use that instead
        // NOTE: Not directly using `default_field_id` because we can't trust it
        if (!fieldId && default_field_id) {
          fieldId = fields?.find(
            (field, fieldIndex) =>
              field.fid === default_field_id.toString() ||
              fieldIndex === default_field_id
          )?.fid;
        }
        // Attempt 3:
        // Field (still) not found, use the `fid` of the first field available
        if (!fieldId) {
          fieldId = fields?.[0]?.fid;
        }
        //
        // Fallback:
        if (!fieldId) {
          // NOTE: We should never reach this point because we usually expect
          // `response.data.fields` (`fields`) to have at least one item (and
          // to exist in the first place) and so falling back to `"0"` is
          // merely a type-safety exercise.
          fieldId = "0";
        }
        // If the above attempts return a different value to the original value
        // for `currentField`, update it...
        if (fieldId !== currentField) {
          // Set field in Redux
          dispatch(
            setCurrentField({
              fieldId,
            })
          );
        }
      } else {
        console.error(response.errors);
      }
      // NOTE: Deliberate delay
      // There is some issue with krpano not being ready and so no scenes are actually added.
      setTimeout(() => {
        setFarmLoaded(true);
      }, 100);
    };
    if (krpanoLoaded && guid) {
      getFarmDetails(guid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [krpanoLoaded, guid, dispatch]);

  return farmLoaded;
};

/**
 * Add fields to krpano scene
 */
const addFieldsToKrpano = (details: FarmDetails) => {
  details.fields?.forEach((field) => {
    const fieldScene = window.krpano.scene.createItem(field.name);
    // if (fieldScene) {
    fieldScene.name = field.name;
    fieldScene.farmname = details.farmname;
    fieldScene.mark_point_in_sphere_feature =
      details.mark_point_in_sphere_feature;
    fieldScene.content = sceneStartingContent;
    for (const [key, value] of Object.entries(field)) {
      if (key === "title") {
        fieldScene[key] = safelyParseComma(value);
      } else {
        fieldScene[key] = value;
      }
    }
  });
};
